import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  DEFAULT_USER_PREFERENCES,
  normalizeUserPreferences,
  parseItemsPerPage,
} from '../userPrefs/userPrefsDefaults';
import {
  clearRuntimeUserPreferences,
  setRuntimeUserPreferences,
} from '../userPrefs/userPrefsRuntime';
import {
  fetchUserPrefOptions,
  fetchUserPrefs,
  saveUserPrefs as saveUserPrefsApi,
} from '../../utils/userPrefsApi';

const UserPrefsContext = createContext(null);

export const useUserPrefs = () => {
  const context = useContext(UserPrefsContext);
  if (!context) {
    throw new Error('useUserPrefs must be used within a UserPrefsProvider');
  }
  return context;
};

export const UserPrefsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [preferences, setPreferences] = useState({ ...DEFAULT_USER_PREFERENCES });
  const [options, setOptions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const applyPreferences = useCallback((nextPreferences) => {
    const normalized = normalizeUserPreferences(nextPreferences);
    setPreferences(normalized);
    setRuntimeUserPreferences(normalized);
    return normalized;
  }, []);

  const clearPreferences = useCallback(() => {
    setPreferences({ ...DEFAULT_USER_PREFERENCES });
    setOptions({});
    setError(null);
    setHasLoaded(false);
    clearRuntimeUserPreferences();
  }, []);

  const loadUserPrefs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [prefs, prefOptions] = await Promise.all([
        fetchUserPrefs(),
        fetchUserPrefOptions(),
      ]);
      applyPreferences(prefs);
      setOptions(prefOptions || {});
      setHasLoaded(true);
      return prefs;
    } catch (err) {
      console.error('[UserPrefsContext] Failed to load user preferences:', err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        'Failed to load preferences.';
      setError(typeof message === 'string' ? message : 'Failed to load preferences.');
      applyPreferences(DEFAULT_USER_PREFERENCES);
      setHasLoaded(true);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [applyPreferences]);

  const saveUserPrefs = useCallback(
    async (nextPreferences) => {
      setIsSaving(true);
      setError(null);
      try {
        const saved = await saveUserPrefsApi(nextPreferences);
        applyPreferences(saved);
        return saved;
      } catch (err) {
        console.error('[UserPrefsContext] Failed to save user preferences:', err);
        const message =
          err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          'Failed to save preferences.';
        setError(typeof message === 'string' ? message : 'Failed to save preferences.');
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [applyPreferences],
  );

  useEffect(() => {
    if (isAuthenticated) {
      loadUserPrefs().catch(() => {
        // Error state is stored in context; app continues with defaults.
      });
    } else {
      clearPreferences();
    }
  }, [isAuthenticated, loadUserPrefs, clearPreferences]);

  const getPageSize = useCallback(
    () => parseItemsPerPage(preferences.itemsPerPage),
    [preferences.itemsPerPage],
  );

  const value = useMemo(
    () => ({
      preferences,
      options,
      isLoading,
      isSaving,
      error,
      hasLoaded,
      loadUserPrefs,
      saveUserPrefs,
      getPageSize,
      setError,
    }),
    [
      preferences,
      options,
      isLoading,
      isSaving,
      error,
      hasLoaded,
      loadUserPrefs,
      saveUserPrefs,
      getPageSize,
    ],
  );

  return (
    <UserPrefsContext.Provider value={value}>{children}</UserPrefsContext.Provider>
  );
};
